<template>
	<div
		ref="root"
		class="custom-infinite-scroll"
		:class="{ 'custom-infinite-scroll--hidden-spinner': !spinner }"
		data-allow-mismatch="class"
	>
		<ClientOnly>
			<Spinner v-if="spinner" class="custom-infinite-scroll__spinner" name="dots" />
		</ClientOnly>
	</div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Spinner from '@/ui-components/Spinner.vue';

interface CustomInfiniteScrollProps {
	/**
	 * The threshold distance from the bottom
	 * of the content to call the `infinite` output event when scrolled.
	 * The threshold value can be either a percent, or
	 * in pixels. For example, use the value of `10%` for the `infinite`
	 * output event to get called when the user has scrolled 10%
	 * from the bottom of the page. Use the value `100px` when the
	 * scroll is within 100 pixels from the bottom of the page.
	 */
	threshold?: string;

	/**
	 * If `true`, the infinite scroll will be hidden and scroll event listeners
	 * will be removed.
	 *
	 * Set this to true to disable the infinite scroll from actively
	 * trying to receive new data while scrolling. This is useful
	 * when it is known that there is no more data that can be added, and
	 * the infinite scroll is no longer needed.
	 */
	disabled?: boolean;
	spinner?: boolean;
	scrollElement?: HTMLElement | null;
}

const props = withDefaults(defineProps<CustomInfiniteScrollProps>(), {
	threshold: '15%',
	disabled: false,
	spinner: false,
	scrollElement: null,
});

const emit = defineEmits(['customInfinite']);

const root = ref<HTMLElement | null>(null);
const thrPx = ref(0);
const thrPc = ref(0);
const scrollEl = ref<HTMLElement | null>(null);
const isLoading = ref(false);
const scrollingRetries = ref(0);
const currentListLen = ref(0);
const lastListLen = ref(0);
const isNotGrowing = ref(false);

/**
 * The threshold distance from the bottom
 * of the content to call the `infinite` output event when scrolled.
 * The threshold value can be either a percent, or
 * in pixels. For example, use the value of `10%` for the `infinite`
 * output event to get called when the user has scrolled 10%
 * from the bottom of the page. Use the value `100px` when the
 * scroll is within 100 pixels from the bottom of the page.
 */
function thresholdChanged() {
	if (props.threshold.lastIndexOf('%') > -1) {
		thrPx.value = 0;
		thrPc.value = parseFloat(props.threshold) / 100;
	} else {
		thrPx.value = parseFloat(props.threshold);
		thrPc.value = 0;
	}
}

watch(() => props.threshold, thresholdChanged);
watch(
	() => props.disabled,
	disabled => {
		disabled ? reset() : refresh();
		toggleScrollEventListener(!disabled);
	}
);

onMounted(() => {
	const contentEl = root.value?.closest('.jw-content') || root.value?.closest('.content');
	if (!contentEl) {
		console.error('<CustomInfiniteScroll> must be used inside a .jw-content or .content container');
		return;
	}

	scrollEl.value = contentEl as HTMLElement;
	thresholdChanged();
	toggleScrollEventListener(!props.disabled);
});

onBeforeUnmount(() => {
	toggleScrollEventListener(false);
	scrollEl.value = null;
});

function reset() {
	// We don't reset isLoading because it would open the door to race conditions.
	// this.isLoading = false;
	isNotGrowing.value = false;
	currentListLen.value = 0;
	lastListLen.value = 0;
	scrollingRetries.value = 0;
}

function canStart(): boolean {
	// We need the component.
	if (!scrollEl.value) {
		return false;
	}

	// We need the component to be displayed.
	if (scrollEl.value.offsetHeight === 0 && scrollEl.value.offsetWidth === 0) {
		return false;
	}

	return !props.disabled && !isNotGrowing.value && !isLoading.value;
}

function toggleScrollEventListener(shouldListen: boolean) {
	const scrollEl = props.scrollElement ?? window;
	if (scrollEl) {
		if (shouldListen) {
			scrollEl.addEventListener('scroll', refresh);
		} else {
			scrollEl.removeEventListener('scroll', refresh);
		}
	}
}

function refresh() {
	if (!canStart()) return 1;

	const height = props.scrollElement ? props.scrollElement.offsetHeight : window.innerHeight;
	const threshold = thrPc.value !== 0 ? height * thrPc.value : thrPx.value;

	const scrollPosition = props.scrollElement ? props.scrollElement.scrollTop : window.scrollY;

	const distanceFromTrigger = (root.value?.offsetTop ?? 0) - threshold - scrollPosition - height;

	if (distanceFromTrigger < 0) {
		if (isLoading.value) return 4;

		isLoading.value = true;

		// We check that the new pages are making the scrollable content grow.
		// We retry (2 times for now, needed for the watchlist view) before blocking completely the infinite scroll.
		if (scrollingRetries.value < 2) {
			if (currentListLen.value === lastListLen.value) {
				scrollingRetries.value++;
			} else {
				scrollingRetries.value = 0;
			}
		} else {
			isNotGrowing.value = true;
			isLoading.value = false;
			return 2;
		}

		emit('customInfinite', { target: { complete } });
		return 3;
	}

	return 4;
}

/**
 * Call `complete()` within the `customInfinite` output event handler when
 * your async operation has completed. For example, the `loading`
 * state is while the app is performing an asynchronous operation,
 * such as receiving more data from an AJAX request to add more items
 * to a data list. Once the data has been received and UI updated, you
 * then call this method to signify that the loading has completed.
 * This method will change the infinite scroll's state from `loading`
 * to `enabled`.
 */
async function complete(listLen: number) {
	if (!isLoading.value || !scrollEl.value) return;

	isLoading.value = false;

	if (listLen) {
		lastListLen.value = currentListLen.value;
		currentListLen.value = listLen;
	}

	// We make sure that after the data fetching is complete, we're still not missing content.
	refresh();
}

defineExpose({ reset, refresh });
</script>

<style lang="scss" scoped>
.custom-infinite-scroll {
	// On list articles this was making the page twice as long with
	// the bottom just being empty space tested infinite scroll
	// on popular, new, and home without issues.

	// If you've come here trying to solve an issue...
	// Uncomment the below and make sure list articles are fine too.

	// Look it was a JW style "this needs to go out" thing, I'm sure you get it.

	// height: 100%;
	text-align: center;

	&--hidden-spinner {
		transform: translateY(100px);
	}

	&__spinner {
		min-height: 100%;
	}
}
</style>
